import { Navigate, Outlet } from 'react-router-dom';

import { Flex, Spinner } from '@kognitos/lattice';
import {
  sentryCreateBrowserRouter,
  RouteErrorBoundary,
} from '@/modules/sentry';
import Layout from '@/layouts/MainLayout';
import { FullPageLoader } from '@/shared/components';
import { getRoutePattern, PARENT_ROUTES } from '@/routes/util';
import { withProtectedRoute } from '@/shared/auth';
import { FinanceLayout } from '@/layouts/FinanceLayout';
import { LegacyTroubleshootingRedirect } from '@/routes/LegacyTroubleshootingRedirect';
import { dropAbandonedManagedOAuthMarker } from '@/modules/credentials/oauth-resume';

const ProtectedLayout = withProtectedRoute(Layout);

const ProtectedNoChromeLayout = withProtectedRoute(Outlet);

// Before the router reads the URL.
dropAbandonedManagedOAuthMarker();

const router = sentryCreateBrowserRouter([
  {
    path: getRoutePattern('HOME'),
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: FullPageLoader,
    children: [
      {
        index: true,
        element: (
          // This is a temporary loading screen for the home route
          // We will redirect user to Chats page from ProtectedLayout HOC
          <Flex justify="center" align="center" className="w-full min-h-screen">
            <Spinner size="xl" variant="default" />
          </Flex>
        ),
      },
      {
        path: getRoutePattern('ONBOARDING'),
        lazy: () => import('@/pages/onboarding/Page'),
      },
      {
        path: getRoutePattern('INVITATION'),
        lazy: () => import('@/pages/onboarding/invitation/Page'),
      },
      {
        path: getRoutePattern('PREFERENCES'),
        lazy: () => import('@/pages/preferences/Page'),
      },
      {
        path: getRoutePattern('TUTORIALS'),
        lazy: () => import('@/pages/tutorials/Page'),
      },
      {
        path: getRoutePattern('USER_SETTINGS'),
        lazy: () => import('@/pages/settings/user/Page'),
        children: [
          {
            index: true,
            element: <Navigate to="profile" replace />,
          },
          {
            path: getRoutePattern('USER_SETTINGS_PROFILE'),
            lazy: () => import('@/pages/settings/user/profile/Page'),
          },
        ],
      },
      {
        path: '*',
        lazy: () => import('@/pages/not-found/Page'),
      },
    ],
  },
  // Organization routes (orgId only)
  {
    path: PARENT_ROUTES.ORG,
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: FullPageLoader,
    children: [
      {
        index: true,
        lazy: () => import('@/pages/not-found/Page'),
      },
      {
        path: getRoutePattern('ORGANIZATION_PERFORMANCE'),
        lazy: () => import('@/pages/organization/performance/Page'),
      },
      {
        path: getRoutePattern('ORGANIZATION_CONSUMPTION'),
        lazy: () => import('@/pages/organization/consumption/Page'),
      },
      {
        path: getRoutePattern('ROLES_AND_ACCESS'),
        lazy: () => import('@/pages/roles-and-access/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS'),
        lazy: () => import('@/pages/remote-connections/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS_CREATE'),
        lazy: () => import('@/pages/remote-connections/create/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS_DETAIL'),
        lazy: () => import('@/pages/remote-connections/detail/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS_EDIT'),
        lazy: () => import('@/pages/remote-connections/edit/Page'),
      },
      {
        path: getRoutePattern('API_KEYS'),
        lazy: () => import('@/pages/api-keys/Page'),
      },
      {
        path: getRoutePattern('AUDIT_LOG'),
        lazy: () => import('@/pages/audit-log/Page'),
      },
      // Org Settings
      {
        path: getRoutePattern('ORG_SETTINGS'),
        lazy: () => import('@/pages/settings/Page'),
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />,
          },
          {
            path: getRoutePattern('ORG_SETTINGS_OVERVIEW'),
            lazy: () => import('@/pages/settings/org/overview/Page'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_WORKSPACES'),
            lazy: () => import('@/pages/settings/org/workspaces/Page'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_PEOPLE'),
            lazy: () =>
              import('@/pages/roles-and-access/redirects/OrgUsersRedirect'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_PREFERENCES'),
            lazy: () => import('@/pages/settings/org/preferences/Page'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_SUPPORT_ACCESS'),
            lazy: () => import('@/pages/settings/org/support-access/Page'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_REMOTE_SUPPORT'),
            lazy: () => import('@/pages/settings/org/remote-support/Page'),
          },
        ],
      },
    ],
  },
  // Workspace routes (orgId + workspaceId)
  {
    path: PARENT_ROUTES.WORKSPACE,
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: FullPageLoader,
    children: [
      {
        path: getRoutePattern('WORKSPACE_ACCESS_DENIED'),
        lazy: () => import('@/pages/workspace/access-denied/Page'),
      },
      // Finance surfaces render only in a finance-typed workspace with the
      // enable_finance_workspace gate on (FinanceLayout).
      {
        element: <FinanceLayout />,
        children: [
          {
            path: getRoutePattern('FINANCE_EXCEPTIONS'),
            lazy: () => import('@/pages/workspace/finance/exceptions/Page'),
          },
          {
            path: getRoutePattern('FINANCE_EXCEPTION_DETAIL'),
            lazy: () =>
              import('@/pages/workspace/finance/exceptions/detail/Page'),
          },
          {
            path: getRoutePattern('FINANCE_ARCHITECTURE'),
            lazy: () => import('@/pages/workspace/finance/architecture/Page'),
          },
          {
            path: getRoutePattern('FINANCE_TOUCHLESS'),
            lazy: () => import('@/pages/workspace/finance/touchless/Page'),
          },
          {
            path: getRoutePattern('FINANCE_AUDIT'),
            lazy: () => import('@/pages/workspace/finance/audit/Page'),
          },
          {
            path: getRoutePattern('FINANCE_INTAKE'),
            lazy: () => import('@/pages/workspace/finance/intake/Page'),
          },
          {
            path: getRoutePattern('FINANCE_INTAKE_DETAIL'),
            lazy: () => import('@/pages/workspace/finance/intake/detail/Page'),
          },
          {
            path: getRoutePattern('FINANCE_ADMIN_CALIBRATION'),
            lazy: () =>
              import('@/pages/workspace/finance/admin-calibration/Page'),
          },
          {
            path: getRoutePattern('FINANCE_ADMIN_COMPLIANCE'),
            lazy: () =>
              import('@/pages/workspace/finance/admin-compliance/Page'),
          },
          {
            path: getRoutePattern('FINANCE_ADMIN_SETTINGS'),
            lazy: () => import('@/pages/workspace/finance/admin-settings/Page'),
          },
          {
            path: getRoutePattern('FINANCE_ADMIN_CONTROLS'),
            lazy: () => import('@/pages/workspace/finance/admin-controls/Page'),
          },
          {
            path: getRoutePattern('FINANCE_APPROVALS'),
            lazy: () => import('@/pages/workspace/finance/approvals/Page'),
          },
          {
            path: getRoutePattern('FINANCE_JOURNAL_ENTRIES'),
            lazy: () =>
              import('@/pages/workspace/finance/journal-entries/Page'),
          },
          {
            // Static `/new` segment — must precede the `/:jeId` detail route so
            // 'new' isn't captured as an entry id.
            path: getRoutePattern('FINANCE_JOURNAL_ENTRY_NEW'),
            lazy: () =>
              import('@/pages/workspace/finance/journal-entries/new/Page'),
          },
          {
            path: getRoutePattern('FINANCE_JOURNAL_ENTRY_DETAIL'),
            lazy: () =>
              import('@/pages/workspace/finance/journal-entries/detail/Page'),
          },
          {
            // THE live cockpit. `cockpit-poc/` is `cockpit-v2/`'s shared data
            // layer, not the old page, so both directory names stay as they
            // are; renaming them is a separate change.
            path: getRoutePattern('FINANCE_COCKPIT'),
            lazy: () => import('@/pages/workspace/finance/cockpit-v2/Page'),
          },
          {
            // Both legacy spellings of the path above were shared widely, so
            // neither may 404. One redirect serves both.
            path: getRoutePattern('FINANCE_COCKPIT_POC'),
            lazy: () =>
              import('@/pages/workspace/finance/cockpit-poc/Redirect'),
          },
          {
            path: getRoutePattern('FINANCE_COCKPIT_V2'),
            lazy: () =>
              import('@/pages/workspace/finance/cockpit-poc/Redirect'),
          },
          {
            path: getRoutePattern('FINANCE_EVIDENCE'),
            lazy: () => import('@/pages/workspace/finance/evidence/Page'),
          },
          {
            path: getRoutePattern('FINANCE_FLEET'),
            lazy: () => import('@/pages/workspace/finance/fleet/Page'),
          },
          {
            path: getRoutePattern('FINANCE_FLEET_DISCOVERY'),
            lazy: () =>
              import('@/pages/workspace/finance/fleet/discovery/Page'),
          },
          {
            path: getRoutePattern('FINANCE_FLEET_WORKSTREAM'),
            lazy: () =>
              import('@/pages/workspace/finance/fleet/workstream/Page'),
          },
          {
            path: getRoutePattern('FINANCE_AGENT_DETAIL'),
            lazy: () => import('@/pages/workspace/finance/agent/detail/Page'),
          },
        ],
      },
      {
        path: getRoutePattern('DRAFTS'),
        lazy: () => import('@/pages/workspace/drafts/Page'),
      },
      {
        path: getRoutePattern('DRAFT_NEW'),
        lazy: () => import('@/pages/workspace/draft/new/Page'),
      },
      {
        path: getRoutePattern('DRAFT_EDITOR'),
        lazy: () => import('@/pages/workspace/draft/edit/Page'),
      },
      {
        path: getRoutePattern('DRAFT_ACTIVITY'),
        lazy: () => import('@/pages/workspace/draft/activity/Page'),
      },
      {
        path: getRoutePattern('DRAFT_PRE_RUN'),
        lazy: () => import('@/pages/workspace/draft/pre-run/Page'),
      },
      {
        path: getRoutePattern('AUTOMATIONS'),
        lazy: () => import('@/pages/workspace/automations/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_AOP'),
        // The draft editor's page, not a copy: React keeps it mounted across
        // the two routes, so switching versions doesn't reload the editor.
        lazy: () => import('@/pages/workspace/draft/edit/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_DETAILS'),
        lazy: () => import('@/pages/workspace/automation/details/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_PRE_RUN'),
        lazy: () => import('@/pages/workspace/automation/pre-run/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_RUN'),
        lazy: () => import('@/pages/workspace/automation/run/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_STEP_WORKBENCH'),
        lazy: () => import('@/pages/workspace/step-workbench/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_WORKBENCH'),
        lazy: () => import('@/pages/workspace/automation-workbench/Page'),
      },
      {
        path: getRoutePattern('DRAFT_RUN'),
        lazy: () => import('@/pages/workspace/draft/run/Page'),
      },
      // Rain Agents — dashboard (`/agents`), the create page with the template
      // gallery (`/agents/new`), one agent's detail page and threads console
      // (`/agents/:agentId`), and the conversational builder for that agent
      // (`/agents/:agentId/build`). The static routes are listed first so they
      // win over the `:agentId` param match.
      {
        path: getRoutePattern('AGENTS'),
        lazy: () => import('@/pages/agents/Page'),
      },
      // Apps: the catalog (`/apps`) and one app (`/apps/:appId`).
      {
        path: getRoutePattern('APPS'),
        lazy: () => import('@/pages/apps/Page'),
      },
      {
        path: getRoutePattern('APP_DETAIL'),
        lazy: () => import('@/pages/apps/detail/Page'),
      },
      {
        path: getRoutePattern('AGENT_NEW'),
        lazy: () => import('@/pages/agents/new/Page'),
      },
      {
        path: getRoutePattern('AGENT_CANVAS_PREVIEW'),
        lazy: () => import('@/pages/agents/canvas/PreviewPage'),
      },
      {
        path: getRoutePattern('AGENT_CANVAS'),
        lazy: () => import('@/pages/agents/canvas/Page'),
      },
      {
        path: getRoutePattern('AGENT_BUILD'),
        lazy: () => import('@/pages/agents/detail/Page'),
      },
      {
        path: getRoutePattern('AGENT_BUILD_EDIT'),
        lazy: () => import('@/pages/agents/edit/Page'),
      },
      {
        path: getRoutePattern('CARTOGRAPHER'),
        lazy: () => import('@/pages/workspace/cartographer/Page'),
      },
      {
        path: getRoutePattern('CONTEXT_GRAPH'),
        lazy: () => import('@/pages/workspace/cartographer/definition/Page'),
      },
      {
        path: getRoutePattern('CONTEXT_GRAPH_ACTIVITY'),
        lazy: () => import('@/pages/workspace/cartographer/activity/Page'),
      },
      {
        path: getRoutePattern('LEARNINGS'),
        lazy: () => import('@/pages/workspace/learnings/Page'),
      },
      {
        path: getRoutePattern('GUIDANCE_CENTER'),
        lazy: () => import('@/pages/workspace/guidance-center/Page'),
      },
      {
        path: getRoutePattern('WORKBENCH'),
        lazy: () => import('@/pages/workspace/workbench/Page'),
      },

      {
        path: getRoutePattern('LEGACY_TROUBLESHOOTING_GUIDE'),
        element: <LegacyTroubleshootingRedirect />,
      },
      {
        path: getRoutePattern('RESOLUTION_PLAYBOOK'),
        lazy: () =>
          import('@/pages/workspace/automation/resolution-playbook/Page'),
        children: [
          {
            index: true,
            lazy: () =>
              import(
                '@/pages/workspace/automation/resolution-playbook/unselected/Page'
              ),
          },
          {
            path: getRoutePattern('RESOLUTION_PLAYBOOK_ENTRY_DETAILS'),
            lazy: () =>
              import(
                '@/pages/workspace/automation/resolution-playbook/entry-details/Page'
              ),
          },
        ],
      },
      {
        path: getRoutePattern('ACTION_ITEM_DETAIL'),
        lazy: () =>
          import('@/pages/workspace/guidance-center/action-item/Page'),
      },
      {
        path: getRoutePattern('ACTION_ITEM_REVIEW'),
        lazy: () =>
          import('@/pages/workspace/guidance-center/action-item-review/Page'),
      },

      // Integrations routes
      {
        path: getRoutePattern('INTEGRATIONS'),
        lazy: () => import('@/layouts/IntegrationsLayout'),
        children: [
          {
            path: '',
            lazy: () => import('@/layouts/IntegrationsViewLayout'),
            children: [
              {
                index: true,
                lazy: () => import('@/pages/workspace/integrations/all/Page'),
              },
              {
                path: getRoutePattern('INTEGRATIONS_CONNECTIONS'),
                lazy: () =>
                  import('@/pages/workspace/integrations/connections/Page'),
              },
            ],
          },
          {
            path: getRoutePattern('INTEGRATIONS_INTEGRATION_DETAILS'),
            lazy: () =>
              import('@/pages/workspace/integrations/integration-details/Page'),
          },
        ],
      },

      // Library — index (root) and :folderId share the same Page; the bundler
      // dedupes the dynamic import via its module cache.
      {
        path: getRoutePattern('LIBRARY'),
        lazy: () => import('@/pages/workspace/library/Page'),
        children: [
          {
            index: true,
            lazy: () => import('@/pages/workspace/library/assets/Page'),
          },
          {
            path: getRoutePattern('LIBRARY_FOLDER'),
            lazy: () => import('@/pages/workspace/library/assets/Page'),
          },
          {
            path: getRoutePattern('LIBRARY_COLLECTIONS'),
            lazy: () => import('@/pages/workspace/library/collections/Page'),
          },
        ],
      },
      {
        path: getRoutePattern('LIBRARY_COLLECTION'),
        lazy: () =>
          import('@/pages/workspace/library/collections/collection-item/Page'),
      },
      // Finance workspace — pages render only in the seeded "finance"
      // workspace (the nav entry is gated by useIsFinanceWorkspace); the
      // routes themselves are always registered but harmless elsewhere.
      {
        path: getRoutePattern('FINANCE'),
        element: <FinanceLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="findings" replace />,
          },
          {
            path: getRoutePattern('FINANCE_FINDINGS'),
            lazy: () => import('@/pages/workspace/finance/findings/Page'),
          },
          {
            path: getRoutePattern('FINANCE_DETECTORS'),
            lazy: () => import('@/pages/workspace/finance/detectors/Page'),
          },
          {
            path: getRoutePattern('FINANCE_INVESTIGATIONS'),
            lazy: () => import('@/pages/workspace/finance/investigations/Page'),
            children: [
              {
                index: true,
                lazy: () =>
                  import(
                    '@/pages/workspace/finance/investigations/portfolio/Page'
                  ),
              },
            ],
          },
          // Case files live under their finding, mirroring argus's resource tree.
          // They share the Investigations shell so the case-file rail stays put
          // when one is opened.
          {
            path: getRoutePattern('FINANCE_FINDING_INVESTIGATIONS'),
            lazy: () => import('@/pages/workspace/finance/investigations/Page'),
            children: [
              {
                index: true,
                lazy: () =>
                  import(
                    '@/pages/workspace/finance/investigations/resolve/Page'
                  ),
              },
              {
                path: getRoutePattern('FINANCE_FINDING_INVESTIGATION'),
                lazy: () =>
                  import(
                    '@/pages/workspace/finance/investigations/detail/Page'
                  ),
              },
            ],
          },
          {
            path: getRoutePattern('FINANCE_LEARNINGS'),
            lazy: () => import('@/pages/workspace/finance/learnings/Page'),
          },
          {
            path: getRoutePattern('FINANCE_CONNECT'),
            lazy: () => import('@/pages/workspace/finance/connect/Page'),
          },
          {
            path: getRoutePattern('FINANCE_ONBOARDING'),
            lazy: () => import('@/pages/workspace/finance/onboarding/Page'),
          },
          {
            path: getRoutePattern('FINANCE_SOP_VAULT'),
            lazy: () => import('@/pages/workspace/finance/sop-vault/Page'),
          },
          {
            path: getRoutePattern('FINANCE_CONTEXT_GRAPH'),
            lazy: () => import('@/pages/workspace/finance/context-graph/Page'),
          },
          {
            path: getRoutePattern('FINANCE_AGENT_NEW'),
            lazy: () => import('@/pages/workspace/finance/agent-new/Page'),
          },
          {
            path: getRoutePattern('FINANCE_RESOLVER'),
            lazy: () => import('@/pages/workspace/finance/resolver/Page'),
          },
          {
            // Folded into the Fleet hub: the per-workstream cockpit is the
            // Fleet Activity lens scoped to that workstream. Redirect there.
            path: getRoutePattern('FINANCE_WORKSTREAM'),
            lazy: () =>
              import('@/pages/workspace/finance/fleet/workstream/Page'),
          },
        ],
      },
      // Workspace Settings
      {
        path: getRoutePattern('WORKSPACE_SETTINGS'),
        lazy: () => import('@/pages/settings/Page'),
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />,
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_OVERVIEW'),
            lazy: () => import('@/pages/settings/workspace/overview/Page'),
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_PEOPLE'),
            lazy: () =>
              import(
                '@/pages/roles-and-access/redirects/WorkspaceUsersRedirect'
              ),
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_PREFERENCES'),
            lazy: () => import('@/pages/settings/workspace/preferences/Page'),
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_NOTIFICATIONS'),
            lazy: () => import('@/pages/settings/workspace/notifications/Page'),
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_NOTIFICATION_CHANNEL'),
            lazy: () =>
              import('@/pages/settings/workspace/notifications/channel/Page'),
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_SUPPORT_ACCESS'),
            lazy: () =>
              import('@/pages/settings/workspace/support-access/Page'),
          },
        ],
      },
    ],
  },
  {
    path: getRoutePattern('ASSUME_SUPPORT'),
    element: <ProtectedNoChromeLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: FullPageLoader,
    children: [
      {
        index: true,
        lazy: () => import('@/pages/assume-support/Page'),
      },
    ],
  },
  {
    path: getRoutePattern('LOGIN'),
    lazy: () => import('@/pages/login/Page'),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: getRoutePattern('CALLBACK'),
    lazy: () => import('@/pages/callback/Page'),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: getRoutePattern('LOGOUT'),
    lazy: () => import('@/pages/logout/Page'),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: getRoutePattern('SIGNUP'),
    lazy: () => import('@/pages/signup/Page'),
    errorElement: <RouteErrorBoundary />,
  },
  // No auth: a fixture-fed view of the AOP map and the run log, for working on
  // either without an automation or a signed-in session behind it. Development
  // only — it is a design harness, not a page anyone should reach in the app.
  ...(import.meta.env.DEV
    ? [
        {
          path: getRoutePattern('PREVIEW_MAP_AND_RUN_LOG'),
          lazy: () => import('@/pages/preview/map-and-run-log/Page'),
          errorElement: <RouteErrorBoundary />,
        },
      ]
    : []),
  {
    path: '*',
    lazy: () => import('@/pages/not-found/Page'),
    errorElement: <RouteErrorBoundary />,
  },
]);

export default router;
